import { ITripRoomGuest } from "@cruisen/contract"
import UserAvatar from "../../atoms/UserAvatar"
import styles from "./styles.module.scss"
import EditOutline from "~icons/eva/edit-outline"
import Trash2Outline from "~icons/eva/trash-2-outline"
import classNames from "classnames"
import { observer } from "mobx-react-lite"
import { useDrag } from "react-dnd"
import { AvailableGuestActions } from "../../molecules/Room"
import { useRef, useState } from "react"
import { OptionPopover } from "../../atoms/OptionPopover"
import LinkOutline from "~icons/eva/link-outline"
import Config from "../../../config"
import { useQuery } from "@tanstack/react-query"
import { generateWebPreviewUrl } from "../../../services/firebase-functions/webPreview"
import { useStores } from "../../../models/helpers/useStores"

export type TripRoomGuestCardItem = {
  id: string
  roomId: string
  tripId: string
  guest: ITripRoomGuest
}

export const TripRoomGuestCard = observer(function TripRoomGuestCard({
  guest,
  testID,
  isProfileIncomplete,
  onGuestActionsClick,
  activePaymentRequest,
  openGuestDetailsModal,
  openDeleteGuestModal,
  guestId,
  guestModal,
  setGuestId,
  setGuestModal,
  roomId,
  tripId,
}: {
  guest: ITripRoomGuest
  testID: string
  isProfileIncomplete: boolean
  onGuestActionsClick?: () => void
  activePaymentRequest: boolean
  openGuestDetailsModal?: () => void
  openDeleteGuestModal?: () => void
  guestId?: string | null
  guestModal?: AvailableGuestActions | null
  setGuestId?: (id: string | null) => void
  setGuestModal?: (modal: AvailableGuestActions | null) => void
  roomId: string
  tripId: string
}) {
  const rootStore = useStores()
  const buttonRef = useRef(null)
  const [isButtonClicked, setIsButtonClicked] = useState(false)

  const [{ isDragging }, drag] = useDrag(() => ({
    type: "tripRoomGuestCard",
    item: () => {
      if (buttonRef.current && buttonRef.current.contains(document.activeElement)) {
        return null
      }

      return {
        id: guest.id || guest.userId,
        roomId,
        tripId,
        guest,
      }
    },
    canDrag: () => {
      return !(buttonRef.current && buttonRef.current.contains(document.activeElement))
    },
    collect: (monitor) => ({
      isDragging: Boolean(monitor.isDragging()),
    }),
  }))

  const joinTripUserId = Config.environment === "production" ? null : guest.userId || null

  const { data: webPreviewUrl } = useQuery({
    queryKey: ["webPreviewUrl", rootStore.activeTrip?.id, joinTripUserId],
    queryFn: () =>
      generateWebPreviewUrl({
        actionId: "openAdvisorInvitedUser",
        userId: joinTripUserId,
        webPreviewData: {
          tripId,
          agentCode: rootStore.loggedInUser?.agentCode,
          tripName: rootStore.activeTrip?.name,
        },
      }),
    staleTime: 0,
    enabled: !!rootStore.activeTrip?.id && !!joinTripUserId,
  })

  const joinTripUrl = webPreviewUrl?.generatedUrl

  const unclaimedProfile = !guest.profileId

  const guestActions = [
    {
      label: "Guest details",
      onClick: openGuestDetailsModal,
      icon: <EditOutline />,
    },
    {
      label: "Delete guest",
      onClick: openDeleteGuestModal,
      icon: <Trash2Outline />,
      isHidden: Boolean(activePaymentRequest),
      danger: true,
    },
    {
      label: "Join trip",
      onClick: () => {
        if (joinTripUrl) {
          window.open(joinTripUrl, "_blank")
        }
      },
      icon: <LinkOutline />,
      isHidden: !joinTripUserId,
    },
  ]

  return (
    <>
      {isDragging ? (
        <div className={classNames(styles.tripRoomGuestCard, styles.dragging)} data-testid={testID}>
          <UserAvatar
            size="sm"
            userId={guest.userId || null}
            avatar={guest.avatar}
            nameInitals={`${guest.firstName[0]}${guest.lastName[0]}`}
          />
          <div className={styles.guestInfoContainer}>
            <div className={styles.guestNameContainer}>
              <p className={styles.guestName} data-testid={`${testID}-name`}>
                {guest.firstName} {guest.lastName}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classNames(
            styles.tripRoomGuestCard,
            activePaymentRequest ? styles.disabled : "",
            isButtonClicked ? styles.buttonClicked : "",
          )}
          data-testid={testID}
          ref={activePaymentRequest ? null : drag}
        >
          <UserAvatar
            size="sm"
            userId={guest.userId || null}
            avatar={guest.avatar}
            nameInitals={`${guest.firstName[0]}${guest.lastName[0]}`}
          />
          <div className={styles.guestInfoContainer}>
            <div className={styles.guestNameContainer}>
              <p className={styles.guestName} data-testid={`${testID}-name`}>
                {guest.firstName} {guest.lastName}
              </p>
              {(isProfileIncomplete || unclaimedProfile) && (
                <p className={styles.incompleteProfile}>
                  {unclaimedProfile ? "Unclaimed profile" : "Incomplete profile"}
                </p>
              )}
            </div>
            <OptionPopover
              options={guestActions}
              isOpen={guestId === guest.id && guestModal === null}
              onClose={(e) => {
                const target = e?.target as HTMLElement
                if (!target || guestModal === "guestDetailsModal") return

                const isInActionPopover = target?.closest("#guest-action-popover") !== null
                const isOptionClicked = (e?.target as { id?: string })?.id.includes("option-")

                if (isOptionClicked || isInActionPopover) return
                setGuestModal(null)
                setGuestId(null)
              }}
              openPopover={(e) => {
                e.stopPropagation()
                onGuestActionsClick()
              }}
              testId={`${testID}-guest-actions`}
              align="end"
              moreButtonRef={buttonRef}
              onMouseDown={(e) => {
                e.stopPropagation()
                setIsButtonClicked(true)
              }}
              onMouseUp={() => {
                setTimeout(() => setIsButtonClicked(false), 100)
              }}
              wrapperClassName={styles.guestActionsPopover}
              moreButtonClassName={styles.guestActionsButton}
            />
          </div>
        </div>
      )}
    </>
  )
})
